import { IconLookup } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useState, useEffect, useCallback, useMemo } from 'react';

interface FontAwesomeIcon {
  id: string;
  label: string;
  unicode: string;
  styles: string[];
}

interface IconChooserProps {
  value?: string;
  onChange: (iconId: string) => void;
  placeholder?: string;
}

const IconChooser: React.FC<IconChooserProps> = ({ 
  value,
  onChange, 
  placeholder = "Search icons..." 
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [icons, setIcons] = useState<FontAwesomeIcon[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const searchIcons = useCallback(async (query: string) => {
    if (!query.trim()) {
      setIcons([]);
      return;
    }

    setIsLoading(true);
    try {
      // search for the given query, but filter down to just free solid icons
      // (unfortunately we can't actually filter *on* free classic solid icons,
      // so we'll just ask for more results than we need and filter client-side)
      const graphQLQuery = `
        {
          search(version: "6.7.2", query: "${query}", first: 20) {
            id, label,
            familyStylesByLicense {
              free {
                style
                family
              }
            }
          }
        }
      `;

      // Note: Replace with actual FontAwesome API endpoint and authentication if needed
      const response = await fetch(`https://api.fontawesome.com/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query: graphQLQuery,
        }),
      });

      const result = await response.json();

      // filter down to just free solid icons and reduce to 10
      const reduced = result.data.search.filter((icon: any) => 
        icon.familyStylesByLicense?.free?.some((style: any) => style.style === 'solid')
      ).map((icon: any) => ({
        id: icon.id,
        label: icon.label
      })).slice(0, 10);;

      setIcons(reduced || []);
    } catch (error) {
      console.error('Error searching icons:', error);
      setIcons([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      searchIcons(searchTerm);
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [searchTerm, searchIcons]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setIsOpen(true);
  };

  const handleIconSelect = (iconId: string) => {
    onChange(iconId);
    setIsOpen(false);
    setSearchTerm('');
  };

  const hasResults = useMemo(() => searchTerm && icons.length > 0, [icons]);

  return (
    <div className="relative flex items-center space-x-2">
      {/* Icon Preview */}
      <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center border rounded">
        {value ? (
          <FontAwesomeIcon icon={{prefix: 'fas',  iconName: value} as IconLookup} />
        ) : (
          <span className="text-gray-400 text-xs">?</span>
        )}
      </div>

      {/* Search Input */}
      <div className="relative flex-1">
        <input
          type="text"
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={() => setIsOpen(true)}
          placeholder={placeholder}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />

        {/* Dropdown */}
        {isOpen && searchTerm && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-120 overflow-y-auto">
            {isLoading ? (
              <div className="px-3 py-2 text-gray-500">Loading...</div>
            ) : icons.length > 0 ? (
              icons.map((icon) => (
                <button
                  key={icon.id}
                  onClick={() => handleIconSelect(icon.id)}
                  className="w-full px-3 py-2 text-left hover:bg-gray-100 flex items-center space-x-2"
                >
                  <FontAwesomeIcon icon={{prefix: 'fas',  iconName: icon.id} as IconLookup} />
                  <span>{icon.label} ({icon.id})</span>
                </button>
              ))
            ) : searchTerm && (
              <div className="px-3 py-2 text-gray-500">No icons found</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default IconChooser;
